import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  TrackingFields,
  TrackingConditionnementInput,
  TrackingDateEnCoursInput,
  TrackingDateFiniInput,
  TrackingSaveButton,
  TrackingStatutSelect,
} from "./TrackingEditCells";

export default function ConsultationAmorceur() {
  const { t, ui } = useI18n();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pdfBusyPn, setPdfBusyPn] = useState("");
  const navigate = useNavigate();

  // --- Contrôles tri / filtre ---
  const [sortKey, setSortKey] = useState("index");
  const [sortDir, setSortDir] = useState("asc");
  const [filterKey, setFilterKey] = useState("");
  const [filterValue, setFilterValue] = useState("");

  const [filterKey2, setFilterKey2] = useState("");
  const [filterValue2, setFilterValue2] = useState("");
  const [onlyRestantPositif, setOnlyRestantPositif] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  function applyFilter(arr, key, value) {
    if (!key || String(value).trim() === "") return arr;
    const needle = String(value).toLowerCase();
    return arr.filter((r) => {
      const v = r[key];
      if (v == null) return false;
      return String(v).toLowerCase().includes(needle);
    });
  }

  // --- Helpers formatage ---
  const rmAccents = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const asFrNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v))
      return v.toLocaleString("fr-FR", { useGrouping: false });
    if (typeof v === "string") {
      const n = Number(v.replace(",", "."));
      if (Number.isFinite(n))
        return n.toLocaleString("fr-FR", { useGrouping: false });
    }
    return v;
  };

  const toCsvValue = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  async function parseJsonResponse(res) {
    try {
      return await res.json();
    } catch {
      return {};
    }
  }

  // --- Export CSV ---
  function handleExportCsv(viewRows) {
    const headers = [
      "INDEX",
      "PN",
      "Conditionnement",
      "NbSacsLot",
      "NumeroSac",
      "ReferenceFournisseur",
      "NoBatch",
      "AMORCEUR",
      "FOURNISSEUR",
      "DATE RECEPTION",
      "DATE PEREMPTION",
      "PRIX SAC (€)",
      "PRIX / KG (€)",
      "PRIX TOTAL",
      "STATUT",
    ].map(rmAccents);

    const data = viewRows.map((r) => [
      r.index,
      r.pn,
      r.NbSacsLot,
      r.NumeroSac,
      r.NoBatch,
      r.ReferenceFournisseur,
      r.Conditionnement,
      r.dimamorceur,
      r.fournisseur,
      r.dateReception,
      r.datePeremption,
      asFrNumber(r.prixSac),
      asFrNumber(r.prixKg),
      asFrNumber(r.PrixTotal),
      r.statut,
    ]);

    const csvBody = [headers, ...data]
      .map((row) => row.map(toCsvValue).join(";"))
      .join("\n");
    const csv = `sep=;\n${csvBody}`;
    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    const d = new Date();
    const ts = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(d.getDate()).padStart(2, "0")}`;
    a.href = URL.createObjectURL(blob);
    a.download = `amorceur_stock_${ts}.csv`;
    a.click();
  }

  // --- Chargement ---
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiFetch("/api/amorceur/list");
        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();

        const mapped = (data || []).map((it, i) => ({
          index: it.index || i + 1,
          pn: it.PN,
          NbSacsLot: it.NbSacsLot,
          NumeroSac: it.NumeroSac,
          NoBatch: it.NoBatch,
          ReferenceFournisseur: it.ReferenceFournisseur,
          Conditionnement: it.Conditionnement,
          dimamorceur: it.dimamorceur,
          fournisseur: it.Fournisseurs,
          dateReception: formatDate(it.DateReception),
          datePeremption: formatDate(it.DatePeremption),
          dateEnCours: formatDate(it.Date_en_cours),
          dateFini: formatDate(it.Date_fini),
          conditionnement: it.Conditionnement ?? "",
          prixSac: toNumber(it.PrixSac),
          prixKg: toNumber(it.PrixKg),
          PrixTotal: calcPrixTotalRestant(it),

          statut: it.Statut,
          certif: it.Certif || "",
          certifPdfKey: it.CertifPdfKey || "",
          certifPdfName: it.CertifPdfName || "",
          raw: it,
        }));

        if (!cancelled) setRows(mapped);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Calcul des valeurs uniques pour filtres ---
  const fournisseurs = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.fournisseur).filter(Boolean)),
      ).sort(),
    [rows],
  );

  const view = useMemo(() => {
    let out = [...rows];
    out = applyFilter(out, filterKey, filterValue);
    out = applyFilter(out, filterKey2, filterValue2);
    if (onlyRestantPositif) {
      out = out.filter((r) => {
        const n = Number(String(r.conditionnement ?? "").replace(",", "."));
        return Number.isFinite(n) && n > 0;
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => compareByKey(a, b, sortKey) * dir);
    return out;
  }, [
    rows,
    filterKey,
    filterValue,
    filterKey2,
    filterValue2,
    onlyRestantPositif,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(view.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedView = view.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey, filterValue, filterKey2, filterValue2, onlyRestantPositif, sortKey, sortDir]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const handleEdit = (rawItem) => {
    localStorage.setItem("woodoo_edit_item", JSON.stringify(rawItem));
    navigate("/logistique/reception/amorceur", {
      state: { item: rawItem, edit: true },
    });
  };

  const openCertifPdf = async (key) => {
    if (!key) return;
    try {
      const r = await apiFetch(
        `/api/logistique/amorceur/download-certif?key=${encodeURIComponent(key)}`,
      );
      if (!r.ok) throw new Error("Impossible d'ouvrir le PDF");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message || "Erreur ouverture PDF");
    }
  };

  const handleCertifPdfReplace = async (rawItem, file) => {
    if (!file || !rawItem?.PN) return;
    if (file.type !== "application/pdf") {
      alert("Fichier PDF uniquement");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("PDF max 10 Mo");
      return;
    }
    setPdfBusyPn(rawItem.PN);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("pn", rawItem.PN);
      const upRes = await apiFetch("/api/logistique/amorceur/upload-certif", {
        method: "POST",
        body: fd,
      });
      const up = await parseJsonResponse(upRes);
      if (!upRes.ok) throw new Error(up.message || "Erreur upload");

      const metaRes = await apiFetch("/api/logistique/amorceur/certif-pdf-meta", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          PN: rawItem.PN,
          CertifPdfKey: up.certif_pdf_key,
          CertifPdfName: up.certif_pdf_name || file.name,
        }),
      });
      const meta = await parseJsonResponse(metaRes);
      if (!metaRes.ok) throw new Error(meta.message || "Erreur enregistrement");

      setRows((prev) =>
        prev.map((row) =>
          row.raw.PN === rawItem.PN
            ? {
                ...row,
                certifPdfKey: up.certif_pdf_key,
                certifPdfName: up.certif_pdf_name || file.name,
                raw: {
                  ...row.raw,
                  CertifPdfKey: up.certif_pdf_key,
                  CertifPdfName: up.certif_pdf_name || file.name,
                },
              }
            : row,
        ),
      );
    } catch (e) {
      alert(e.message || "Erreur remplacement PDF");
    } finally {
      setPdfBusyPn("");
    }
  };

  const handleTrackingSaved = (pn, updates) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.raw?.PN !== pn) return r;
        const nextRaw = {
          ...r.raw,
          Date_en_cours: updates.dateEnCours || "",
          Date_fini: updates.dateFini || "",
          Conditionnement: updates.placageRestant,
          Statut: updates.etat ?? r.raw.Statut,
        };
        return {
          ...r,
          dateEnCours: formatDate(updates.dateEnCours),
          dateFini: formatDate(updates.dateFini),
          conditionnement: updates.placageRestant,
          PrixTotal: calcPrixTotalRestant(nextRaw),
          statut: updates.etat ?? r.statut,
          raw: nextRaw,
        };
      }),
    );
  };

  return (
    <div style={styles.page}>
      <MenuBar active="logistique" />
      <div style={styles.wrap} className="wrap">
        <section style={styles.panel} className="card">
          <div style={styles.panelTitle}>SUIVI STOCK AMORCEUR</div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>LOTS AMORCEUR</div>

            <div style={styles.toolbar} className="toolbar">
              <div style={styles.group} className="group">
                <span style={styles.label}>{ui("Trier par :")}</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  style={styles.select}
                >
                  <option value="index">Index</option>
                  <option value="pn">PN</option>
                  <option value="NbSacsLot">NbSacsLot</option>
                  <option value="NumeroSac">NumeroSac</option>
                  <option value="NoBatch">NoBatch</option>
                  <option value="ReferenceFournisseur">
                    ReferenceFournisseur
                  </option>
                  <option value="Conditionnement">Conditionnement</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dimamorceur">Amorceur</option>
                  <option value="dateReception">Date réception</option>
                  <option value="prixSac">Prix / sac</option>
                  <option value="prixKg">Prix / kg</option>
                </select>
                <button
                  onClick={() =>
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  style={styles.button}
                >
                  {sortDir === "asc" ? "↗︎ Asc" : "↘︎ Desc"}
                </button>
              </div>

              <div style={styles.group}>
                <span style={styles.label}>Filtrer :</span>
                <select
                  value={filterKey}
                  onChange={(e) => {
                    setFilterKey(e.target.value);
                    setFilterValue("");
                  }}
                  style={styles.select}
                >
                  <option value="">{ui("— colonne —")}</option>
                  <option value="pn">PN</option>
                  <option value="NbSacsLot">NbSacsLot</option>
                  <option value="NoBatch">NoBatch</option>
                  <option value="NumeroSac">NumeroSac</option>
                  <option value="ReferenceFournisseur">
                    ReferenceFournisseur
                  </option>
                  <option value="Conditionnement">Conditionnement</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dimamorceur">Amorceur</option>
                  <option value="statut">Statut</option>
                </select>
                {filterKey === "fournisseur" ? (
                  <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="valeur…"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.input}
                  />
                )}
              </div>

              {/* --- 2e filtre (AND) --- */}
              <div style={styles.group}>
                <span style={styles.label}>Et :</span>
                <select
                  value={filterKey2}
                  onChange={(e) => {
                    setFilterKey2(e.target.value);
                    setFilterValue2("");
                  }}
                  style={styles.select}
                >
                  <option value="">{ui("— colonne —")}</option>
                  <option value="pn">PN</option>
                  <option value="NbSacsLot">NbSacsLot</option>
                  <option value="NoBatch">NoBatch</option>
                  <option value="NumeroSac">NumeroSac</option>
                  <option value="ReferenceFournisseur">
                    ReferenceFournisseur
                  </option>
                  <option value="Conditionnement">Conditionnement</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dimamorceur">Amorceur</option>
                </select>

                {filterKey2 === "fournisseur" ? (
                  <select
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                ) : filterKey2 === "statut" ? (
                  <select
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {Array.from(new Set(rows.map((r) => r.statut).filter(Boolean))).sort().map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="valeur…"
                    value={filterValue2}
                    onChange={(e) => setFilterValue2(e.target.value)}
                    style={styles.input}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setFilterKey("");
                  setFilterValue("");
                  setFilterKey2("");
                  setFilterValue2("");
                  setOnlyRestantPositif(true);
                  setSortKey("index");
                  setSortDir("asc");
                }}
                style={{
                  ...styles.button,
                  background: "#fff",
                  color: "#000",
                }}
              >
                {ui("Réinitialiser")}
              </button>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#000",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={onlyRestantPositif}
                  onChange={(e) => setOnlyRestantPositif(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                Conditionnement restant &gt; 0
              </label>

              <button
                onClick={() => handleExportCsv(view)}
                style={styles.button}
              >
                {ui("Export CSV")}
              </button>
            </div>

            <div style={styles.paginationInfo}>
              <span style={styles.infoText}>
                Affichage {view.length === 0 ? 0 : startIndex + 1} à{" "}
                {Math.min(endIndex, view.length)} sur {view.length} résultats
              </span>
              <div style={styles.group}>
                <span style={styles.label}>Lignes par page :</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={styles.select}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div style={{ overflowX: "auto" }} className="tableWrap">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>{ui("INDEX")}</Th>
                    <Th>DATE RÉCEPTION</Th>
                    <Th>Amorceur</Th>
                    <Th>{ui("FOURNISSEUR")}</Th>
                    <Th>ReferenceFournisseur</Th>
                    <Th> NoBatch </Th>
                    <Th>PN</Th>
                    <Th>DATE PÉREMPTION</Th>
                    <Th>{ui("DATE EN COURS")}</Th>
                    <Th>{ui("DATE FINI")}</Th>
                    <Th>CONDITIONNEMENT RESTANT (KG)</Th>
                    <Th>NbrSacLot</Th>
                    <Th>NumeroSac</Th>
                    <Th>PRIX / SAC (€)</Th>
                    <Th>PRIX / KG (€)</Th>
                    <Th>PRIX TOTAL(€)</Th>
                    <Th>{ui("STATUT")}</Th>
                    <Th>{ui("CERTIF")}</Th>
                    <Th>{ui("CERTIFICAT PDF")}</Th>
                    <Th>{ui("ACTIONS")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={20} style={styles.loadingCell}>
                        {ui("Chargement…")}
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedView.map((r, idx) => (
                      <TrackingFields
                        key={`${r.pn}-${idx}`}
                        pn={r.raw?.PN}
                        apiPath="/api/amorceur/tracking"
                        dateEnCours={r.raw?.Date_en_cours}
                        dateFini={r.raw?.Date_fini}
                        placageRestant={r.conditionnement}
                        statut={r.statut || r.raw?.Statut}
                        onSaved={(updates) => handleTrackingSaved(r.raw.PN, updates)}
                      >
                      <tr>
                        <Td center>{r.index}</Td>
                        <Td center>{r.dateReception}</Td>
                        <Td center>{r.dimamorceur}</Td>
                        <Td center>{r.fournisseur}</Td>
                        <Td center>{r.ReferenceFournisseur}</Td>
                        <Td center>{r.NoBatch}</Td>
                        <Td center>{r.pn}</Td>
                        <Td center>{r.datePeremption}</Td>
                        <Td center>
                          <TrackingDateEnCoursInput />
                        </Td>
                        <Td center>
                          <TrackingDateFiniInput />
                        </Td>
                        <Td center>
                          <TrackingConditionnementInput />
                        </Td>
                        <Td center>{r.NbSacsLot}</Td>
                        <Td center>{r.NumeroSac}</Td>
                        <Td center>{r.prixSac}</Td>
                        <Td center>{r.prixKg}</Td>
                        <Td center>{r.PrixTotal}</Td>
                        <Td center>
                          <TrackingStatutSelect />
                          <TrackingSaveButton />
                        </Td>
                        <Td center>{r.certif || "—"}</Td>
                        <Td center>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            {r.certifPdfKey ? (
                              <button
                                type="button"
                                style={{
                                  ...styles.actionBtn,
                                  height: 28,
                                  fontSize: 12,
                                }}
                                onClick={() => openCertifPdf(r.certifPdfKey)}
                                title={r.certifPdfName || "Voir le certificat"}
                              >
                                {ui("VOIR PDF")}
                              </button>
                            ) : (
                              <span style={{ color: "#666", fontSize: 12 }}>
                                Aucun
                              </span>
                            )}
                            <label
                              style={{
                                cursor:
                                  pdfBusyPn === r.raw.PN ? "wait" : "pointer",
                                width: "100%",
                              }}
                            >
                              <input
                                type="file"
                                accept="application/pdf"
                                style={{ display: "none" }}
                                disabled={pdfBusyPn === r.raw.PN}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleCertifPdfReplace(r.raw, f);
                                  e.target.value = "";
                                }}
                              />
                              <span
                                style={{
                                  ...styles.actionBtn,
                                  display: "inline-block",
                                  height: 28,
                                  fontSize: 11,
                                  lineHeight: "28px",
                                  opacity: pdfBusyPn === r.raw.PN ? 0.6 : 1,
                                }}
                              >
                                {pdfBusyPn === r.raw.PN
                                  ? "..."
                                  : r.certifPdfKey
                                    ? "REMPLACER"
                                    : "AJOUTER"}
                              </span>
                            </label>
                          </div>
                        </Td>
                        <Td center>
                          <button
                            style={styles.actionBtn}
                            onClick={() => handleEdit(r.raw)}
                          >
                            MODIFIER
                          </button>
                        </Td>
                      </tr>
                      </TrackingFields>
                    ))}

                  {!loading && view.length === 0 && (
                    <tr>
                      <td colSpan={20} style={styles.loadingCell}>
                        Aucune donnée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!loading && view.length > 0 && (
              <div style={styles.paginationControls} className="pagination">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
                  }}
                  title="Première page"
                >
                  ««
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
                  }}
                  title="Page précédente"
                >
                  «
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={`ellipsis-${idx}`} style={styles.ellipsis}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      style={{
                        ...styles.pageBtn,
                        ...(currentPage === page ? styles.pageBtnActive : {}),
                      }}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
                  }}
                  title="Page suivante"
                >
                  »
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
                  }}
                  title="Dernière page"
                >
                  »»
                </button>
              </div>
            )}

            {err && <div style={styles.error}>⚠ {err}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ===== Helpers ===== */
function formatDate(d) {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  }
  return d;
}
function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
}
function toNum(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * Prix total restant amorceur (règle de 3) :
 * prixRestant = PrixTotal × (Conditionnement restant / Conditionnement initial)
 */
function calcPrixTotalRestant(it) {
  const initial = toNum(it.ConditionnementInitial ?? it.Conditionnement);
  const restant =
    it.Conditionnement !== "" && it.Conditionnement != null
      ? toNum(it.Conditionnement)
      : initial;
  const prixTotalInitial = toNum(it.PrixTotal);

  if (initial == null || initial <= 0) return "";
  if (restant == null) return "";
  if (restant <= 0) return 0;

  if (prixTotalInitial != null && prixTotalInitial >= 0) {
    return Math.round(prixTotalInitial * (restant / initial) * 100) / 100;
  }

  const prixKg = toNum(it.PrixKg);
  if (prixKg == null) return "";
  return Math.round(restant * prixKg * 100) / 100;
}
function compareByKey(a, b, key) {
  const va = a[key],
    vb = b[key];
  if (key === "index" || key === "prixSac" || key === "prixKg")
    return (Number(va) || 0) - (Number(vb) || 0);
  return String(va || "").localeCompare(String(vb || ""), "fr", {
    sensitivity: "base",
  });
}
function Th({ children }) {
  const { ui } = useI18n();
  const _c = typeof children === "string" ? ui(children) : children;

  return <th style={styles.th}>{_c}</th>;
}
function Td({ children, center }) {
  return (
    <td
      style={{
        ...styles.td,
        textAlign: center ? "center" : "left",
      }}
    >
      {children}
    </td>
  );
}

/* ===== Styles ===== */
const LIME = "#E6FF3E";
const styles = {
  page: {
    minHeight: "100vh",
    background: "#000",
    color: "#fff",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  wrap: { padding: "16px" },
  panel: { background: "#63686f", border: "1px solid #3c4046", padding: 16 },
  panelTitle: {
    color: "#fff",
    fontWeight: 800,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  card: { background: "#e9eaed", border: "1px solid #c9cbd1" },
  cardTitle: {
    background: "#d8d9db",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    padding: "10px 16px",
    borderBottom: "1px solid #c9cbd1",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderBottom: "1px solid #c9cbd1",
    background: "#f3f4f6",
  },
  group: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  label: { color: "#000", fontWeight: 700, fontSize: 12 },
  select: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  input: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  button: {
    height: 34,
    padding: "0 10px",
    border: "none",
    background: LIME,
    color: "#000",
    fontWeight: 800,
    cursor: "pointer",
  },
  paginationInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    background: "#f3f4f6",
    borderBottom: "1px solid #c9cbd1",
    flexWrap: "wrap",
    gap: 10,
  },
  infoText: {
    color: "#000",
    fontSize: 13,
    fontWeight: 600,
  },
  paginationControls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    padding: "12px",
    background: "#f3f4f6",
    borderTop: "1px solid #c9cbd1",
    flexWrap: "wrap",
  },
  pageBtn: {
    minWidth: 36,
    height: 36,
    padding: "0 10px",
    border: "1px solid #c0c0c0",
    background: "#fff",
    color: "#000",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  pageBtnActive: {
    background: LIME,
    borderColor: LIME,
    fontWeight: 800,
  },
  pageBtnDisabled: {
    background: "#e0e0e0",
    color: "#999",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  ellipsis: {
    padding: "0 8px",
    color: "#000",
    fontWeight: 700,
  },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff" },
  th: {
    background: "#d9d9d9",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    border: "1px solid #b7b7b7",
    padding: "10px 6px",
  },
  td: {
    background: "#fff",
    color: "#000",
    border: "1px solid #c7c7c7",
    padding: "8px 6px",
    fontWeight: 500,
  },
  actionBtn: {
    background: LIME,
    color: "#000",
    fontWeight: 800,
    border: "none",
    width: "100%",
    height: 36,
    cursor: "pointer",
  },
  error: { marginTop: 10, color: "#ffb3b3", fontWeight: 600 },
  loadingCell: { padding: 16, textAlign: "center", background: "#fff" },
};

/* ✅ Responsive media queries */
const mediaStyle = document.createElement("style");
mediaStyle.innerHTML = `
/* --- Mobile --- */
@media (max-width: 639px) {
  .wrap { padding: 8px !important; }
  .toolbar { flex-direction: column !important; align-items: stretch !important; }
  .group { flex-direction: column !important; align-items: stretch !important; width: 100% !important; }
  .tableWrap { overflow-x: auto; }
  table { font-size: 13px !important; }
  th, td { padding: 8px 4px !important; }
  .actionBtn { font-size: 13px !important; height: 40px !important; }
}

/* --- Tablette --- */
@media (min-width: 640px) and (max-width: 1023px) {
  .wrap { padding: 12px 24px !important; }
  .toolbar { flex-wrap: wrap !important; gap: 8px !important; }
}

/* --- Desktop --- */
@media (min-width: 1024px) {
  .wrap { padding: 24px 64px !important; }
  .toolbar { gap: 12px !important; }
  th, td { padding: 10px 8px !important; }
}
`;
document.head.appendChild(mediaStyle);
